import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  AlarmClock,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock,
  LogOut,
  MapPinOff,
  PencilLine,
  Timer,
  Users,
} from 'lucide-react';
import { PageLayout } from '../../../../components/ui/PageLayout';
import {
  EMPTY_VALUE,
  ExportMenu,
  PeriodPicker,
  ReportEmpty,
  ReportError,
  ReportFilterBar,
  ReportFilterField,
  ReportIdle,
  ReportLoading,
  ReportSummaryStrip,
  ReportTable,
  RunButton,
  ScopeFilter,
  StatusChip,
  buildExportRequest,
  dayStatusTone,
  formatCountWithMinutes,
  formatDayShort,
  formatHours,
  formatInt,
  formatMinutes,
  formatPeriod,
  reportExportFilename,
  reportExportLabels,
  resolvePeriodPreset,
  useGroupCollapse,
  useReportExport,
  useScopeFilter,
} from '../../../../components/reports';
import type {
  PeriodValue,
  ReportColumn,
  ReportExportFormat,
  ReportGroup,
  ReportSummaryItem,
} from '../../../../components/reports';
import { postReportExport } from '../../../../api/reports';
import type {
  ClockingPunchView,
  ClockingsDailyRowView,
  ClockingsReportInput,
} from '../../../../models/attendance';
import { ClockingType } from '../../../../models/attendance';
import { ClientType } from '../../../../models/general';
import * as ReportsApi from '../../../../api/Attendance/reports';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { dayKeyPeriod, isCompletePeriod } from '../reportPeriod';

/**
 * Attendance › Clockings, on the shared reports shell — as ONE ROW PER PERSON PER DAY.
 *
 * 🔴 WHAT THIS REPLACED, AND WHY THE OLD SHAPE COULD NOT BE FIXED IN PLACE.
 *
 * The old tab was 643 lines around `components/reportsController`, and its problem was not styling:
 * it printed ONE ROW PER PUNCH. A person who clocked in, took a lunch break and clocked out
 * produced FOUR rows carrying the same name and the same date, and the reader had to do the
 * subtraction in their head to learn the only two things they opened the report for — when the day
 * started and ended, and how long it actually was. Nothing on that shape could total, because
 * summing four timestamps means nothing; the "grouped" mode grouped by day and still printed the
 * punches, and the flat mode paged 25 punches at a time, so a month for ten people was ~50 pages of
 * timestamps. Four further defects came with it:
 *
 *  1. **Every visit fired a year-to-date, company-wide query.** `dateFrom` defaulted to 1 January
 *     and `useEffect(fetchData, [])` ran it on mount, against a Basic/5-DTU database. Nothing runs
 *     here until Run is pressed (`hasRun`).
 *  2. **Two client-side paginations over data the server had already streamed in full** — and the
 *     grouped pager counted only level-0 groups while the flat one counted punches, so the same
 *     report reported two different sizes depending on a toggle.
 *  3. **The AND/OR cascade with its five `selected*Names` mirrors of the same state.** `ScopeFilter`
 *     replaces the whole thing, and this tab finally has the country→branch→department→people
 *     scope the other attendance reports got.
 *  4. **The client rendered the export.** `reportsController` posted to `reports/Clockings/{format}`,
 *     which builds a different document from a different code path (and only xlsx/pdf). Exports now
 *     post the SAME filter the screen ran to `reports/Export/Clockings`, the server re-runs the
 *     query, and all three formats come out of one pipeline. (The legacy route is untouched —
 *     mobile calls it.)
 *
 * 🔴 NOTHING IS LOST IN THE PIVOT. Every punch the old table printed — its time, its source, its
 * address, its comment — is still here, in the row's expandable timeline. What changed is that the
 * timeline is now the DETAIL of an answer instead of being the answer.
 */

/** Below this, a duration is not a figure — 18 seconds, which `formatHours` renders as `0h`. */
const HOUR_EPSILON = 0.005;

/**
 * A wall-clock reading from the wire.
 *
 * ⚠️ READ TEXTUALLY. `firstIn`, `lastOut`, the two scheduled boundaries and every punch time are
 * LOCAL wall-clock values the server stamps in the EMPLOYEE's timezone and serialises with no
 * offset; handing them to `new Date` makes the browser re-interpret them in the READER's timezone,
 * which is a no-op only when reader and employee happen to share one. An HR user in Dubai reading a
 * Cairo employee's 09:00 start would otherwise see 11:00 — and then a "Late" chip on a punctual
 * person. Same rule, same reason, as `Shifts.tsx`.
 */
function wallClock(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /T(\d{2}):(\d{2})/.exec(value);
    if (match) return `${match[1]}:${match[2]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** The LOCAL calendar day of a value, `yyyy-mm-dd`. Read textually for the same reason as above. */
function dayKeyOf(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function workedHoursOf(row: ClockingsDailyRowView): number {
  return (row.workedMinutes ?? 0) / 60;
}

function hasHours(hours: number): boolean {
  return Number.isFinite(hours) && Math.abs(hours) >= HOUR_EPSILON;
}

/**
 * The flags a day carries, worst first — a day nobody clocked out of is the one a reviewer must
 * ACT on, a punch accepted off-site is the one they must JUDGE, and a hand-edited time is the one
 * they must TRUST or not.
 *
 * 🔴 These three tokens ARE the export contract for the `flags` column: the server derives the same
 * three (`ReportsController.ClockingsDayFlagsText`) and looks them up in the `lookups.flags` map
 * this screen sends, so the file reads in the reader's language and says the same thing the screen
 * said. Late / EarlyLeave are deliberately NOT flags — they are the row's status chip.
 */
type ClockingDayFlag = 'MissingOut' | 'OutOfBounds' | 'Edited';

function flagsOf(row: ClockingsDailyRowView): ClockingDayFlag[] {
  const flags: ClockingDayFlag[] = [];
  if (row.missingOut) flags.push('MissingOut');
  if (row.outOfBounds) flags.push('OutOfBounds');
  if (row.manualEdit) flags.push('Edited');
  return flags;
}

const FLAG_TONE: Record<ClockingDayFlag, 'critical' | 'warning' | 'neutral'> = {
  MissingOut: 'critical',
  OutOfBounds: 'warning',
  Edited: 'neutral',
};

const FLAG_ICON = {
  MissingOut: LogOut,
  OutOfBounds: MapPinOff,
  Edited: PencilLine,
} as const;

const PUNCH_ORDER: ClockingType[] = [
  ClockingType.ClockIn,
  ClockingType.BreakIn,
  ClockingType.BreakOut,
  ClockingType.ClockOut,
];

interface ClockingsTotals {
  days: number;
  worked: number;
  breakMinutes: number;
  punches: number;
  missingOut: number;
  lateIn: number;
  unscheduled: number;
  outOfBounds: number;
}

const ZERO_TOTALS: ClockingsTotals = {
  days: 0,
  worked: 0,
  breakMinutes: 0,
  punches: 0,
  missingOut: 0,
  lateIn: 0,
  unscheduled: 0,
  outOfBounds: 0,
};

function addRow(totals: ClockingsTotals, row: ClockingsDailyRowView): ClockingsTotals {
  return {
    days: totals.days + 1,
    worked: totals.worked + workedHoursOf(row),
    breakMinutes: totals.breakMinutes + (row.breakMinutes ?? 0),
    punches: totals.punches + (row.punchCount ?? 0),
    missingOut: totals.missingOut + (row.missingOut ? 1 : 0),
    lateIn: totals.lateIn + (row.lateIn ? 1 : 0),
    unscheduled: totals.unscheduled + (row.dayStatus === 'Unscheduled' ? 1 : 0),
    outOfBounds: totals.outOfBounds + (row.outOfBounds ? 1 : 0),
  };
}

function addTotals(a: ClockingsTotals, b: ClockingsTotals): ClockingsTotals {
  return {
    days: a.days + b.days,
    worked: a.worked + b.worked,
    breakMinutes: a.breakMinutes + b.breakMinutes,
    punches: a.punches + b.punches,
    missingOut: a.missingOut + b.missingOut,
    lateIn: a.lateIn + b.lateIn,
    unscheduled: a.unscheduled + b.unscheduled,
    outOfBounds: a.outOfBounds + b.outOfBounds,
  };
}

interface ClockingsGroup extends ReportGroup<ClockingsDailyRowView> {
  employeeId: string;
  totals: ClockingsTotals;
}

export default function Clockings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { userId, companyId } = useSelector(
    (state: {
      user?: { userId?: string; companyId?: number; company?: { id?: number } };
    }) => ({
      userId: state.user?.userId,
      companyId: state.user?.companyId ?? state.user?.company?.id,
    }),
  );

  /** DAY mode, defaulting to THIS MONTH — see the header note on the old 1-January default. */
  const [period, setPeriod] = useState<PeriodValue>(
    () => resolvePeriodPreset('thisMonth', 'day') ?? { from: '', to: '' },
  );

  const scope = useScopeFilter({ companyId, userId });

  const [loading, setLoading] = useState(false);
  // "Nobody clocked in this range" and "nobody has asked yet" are different screens; `rows.length`
  // cannot tell them apart, so the fact that a report was RUN is held explicitly.
  const [hasRun, setHasRun] = useState(false);
  const [rows, setRows] = useState<ClockingsDailyRowView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const requestSeq = useRef(0);

  const { busy: exporting, exportReport } = useReportExport();

  // MAC-A — an INCOMPLETE period (a cleared date input) is unusable too: see reportPeriod.ts.
  const invalidRange = !isCompletePeriod(period) || period.from > period.to;

  /* ── the request ──────────────────────────────────────────────────────────────────────────── */

  /**
   * 🔴 AN EMPTY `userIds` DOES NOT MEAN "EVERYONE" ON THIS ENDPOINT. `ReportsService`'s clockings
   * scoping reads a request with no userIds AND no department/country/branch filter as **the caller
   * alone**, so the "All employees" default has to name the roster EXPLICITLY or an HR user pressing
   * Run on an unfiltered report would get a report about themselves. (Identical to Shifts — see the
   * long note there.)
   *
   * Naming the roster cannot widen anything: the service intersects whatever arrives with
   * `GetAuthorizedUserIdsAsync` (Hr/Chief ⇒ their own company, Manager ⇒ their team, everyone else
   * ⇒ self) and filters by the caller's `CompanyId` on every branch.
   */
  const buildInput = useCallback((): ClockingsReportInput => {
    const scoped = scope.resolvedUserIds.length > 0 ? scope.resolvedUserIds : scope.roster.map((p) => p.id);
    return {
      userId: userId ?? '',
      // 🔴 MAC-A — the picked DAY KEYS, as strings (`2026-10-12`), never a `Date`. A `Date` travels
      // as a UTC instant and `GetClockingsDailyAsync` takes `.Date` of it: local midnight is the
      // previous UTC day east of Greenwich (an extra day at the START of the report), and 23:59:59
      // is the next one west of it (an extra day at the END). The server matches the days against
      // the employee's own wall clock (`ClockInTime.Date`), `to` inclusive; see reportPeriod.ts.
      ...dayKeyPeriod(period.from, period.to),
      userIds: scoped.length > 0 ? scoped : [userId ?? ''],
      searchMethod: 'AND',
    };
  }, [userId, period.from, period.to, scope.resolvedUserIds, scope.roster]);

  const runReport = useCallback(
    async (input: ClockingsReportInput) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await ReportsApi.getClockingsDaily(input);
        if (seq !== requestSeq.current) return;
        if (result.errors.length > 0) {
          setError(getApiErrorMessage(result, t, t('reports.errorTitle')));
          return;
        }
        setRows(result.payload ?? []);
        setExpanded({});
        setHasRun(true);
      } catch (err) {
        if (seq === requestSeq.current) setError(getApiErrorMessage(err, t, t('reports.errorTitle')));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [t],
  );

  // The roster is the "All employees" list this endpoint needs NAMED (see buildInput). Until the
  // scope hook has loaded it, a run would fall back to the caller alone and answer "Nothing to
  // report" for a whole company. Defer instead.
  const [runWhenScopeReady, setRunWhenScopeReady] = useState(false);

  const search = useCallback(async () => {
    if (invalidRange) return;
    if (scope.loading && scope.resolvedUserIds.length === 0) {
      setRunWhenScopeReady(true);
      setLoading(true);
      setError(null);
      return;
    }
    // A scope the user set that matches nobody must NOT reach the server: it would arrive as an
    // empty list, which this endpoint reads as "the caller", i.e. it would answer a filter naming
    // nobody with the reader's own clockings.
    if (scope.matchedNobody) {
      requestSeq.current += 1;
      setRows([]);
      setError(null);
      setHasRun(true);
      return;
    }
    await runReport(buildInput());
  }, [invalidRange, scope.loading, scope.resolvedUserIds.length, scope.matchedNobody, runReport, buildInput]);

  useEffect(() => {
    if (runWhenScopeReady && !scope.loading) {
      setRunWhenScopeReady(false);
      void search();
    }
  }, [runWhenScopeReady, scope.loading, search]);

  /* ── grouping ─────────────────────────────────────────────────────────────────────────────── */

  const rowKeyOf = useCallback(
    (row: ClockingsDailyRowView) => `${row.userId}|${dayKeyOf(row.date)}`,
    [],
  );

  const groups = useMemo<ClockingsGroup[]>(() => {
    const byUser = new Map<string, { rows: ClockingsDailyRowView[]; totals: ClockingsTotals }>();
    for (const row of rows) {
      const bucket = byUser.get(row.userId) ?? { rows: [], totals: ZERO_TOTALS };
      bucket.rows.push(row);
      bucket.totals = addRow(bucket.totals, row);
      byUser.set(row.userId, bucket);
    }

    return Array.from(byUser.entries()).map(([employeeId, bucket]) => {
      const first = bucket.rows[0];
      // Ascending by day, so a group reads as a diary.
      const ordered = [...bucket.rows].sort((a, b) => dayKeyOf(a.date).localeCompare(dayKeyOf(b.date)));
      const subtitle = [first?.departmentName, first?.userTitle].filter(Boolean).join(' · ');

      return {
        key: employeeId,
        employeeId,
        totals: bucket.totals,
        title: first?.userFullName ?? '',
        subtitle: subtitle || undefined,
        badges: (
          <>
            {/* An unfinished day is the only thing here that makes the row's own worked figure a
                lower bound rather than a fact, so it is said on the group, not only in a cell. */}
            {bucket.totals.missingOut > 0 && (
              <StatusChip tone="critical" data-testid="clockings-group-missing-out">
                {t('reports.clockings.missingOutBadge', { value: bucket.totals.missingOut })}
              </StatusChip>
            )}
            {bucket.totals.lateIn > 0 && (
              <StatusChip tone="warning" data-testid="clockings-group-late">
                {t('reports.clockings.lateBadge', { value: bucket.totals.lateIn })}
              </StatusChip>
            )}
            {bucket.totals.unscheduled > 0 && (
              <StatusChip tone="neutral" data-testid="clockings-group-unscheduled">
                {t('reports.clockings.unscheduledBadge', { value: bucket.totals.unscheduled })}
              </StatusChip>
            )}
          </>
        ),
        rows: ordered,
      };
    });
  }, [rows, t]);

  const totals = useMemo(
    () => groups.reduce((acc, g) => addTotals(acc, g.totals), ZERO_TOTALS),
    [groups],
  );

  const collapse = useGroupCollapse(groups, false);

  /* ── cells ────────────────────────────────────────────────────────────────────────────────── */

  const toggleRow = useCallback(
    (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] })),
    [],
  );

  /** Zero is CONTEXT, not a figure: a column printing `0h` on every row buries the few that carry
   *  hours. The raw value still reaches the totals and the export — only the render changes. */
  const hoursCell = (hours: number, className?: string) =>
    hasHours(hours) ? (
      <span className={className}>{formatHours(hours)}</span>
    ) : (
      <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>
    );

  const timeCell = (value: string | null | undefined) => {
    const text = wallClock(value);
    return text ? <span className="tabular-nums">{text}</span> : (
      <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>
    );
  };

  const sourceLabel = (client: ClientType | undefined) =>
    client === ClientType.Mobile
      ? t('reports.clockings.source.Mobile')
      : client === ClientType.Web
        ? t('reports.clockings.source.Web')
        : client === ClientType.Device
          ? t('reports.clockings.source.Device')
          : '';

  const punchLabel = (type: ClockingType) =>
    PUNCH_ORDER.includes(type) ? t(`reports.clockings.punch.${type}`) : String(type);

  const renderDate = (row: ClockingsDailyRowView) => {
    const key = rowKeyOf(row);
    const day = dayKeyOf(row.date);
    const open = !!expanded[key];
    return (
      <span className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => toggleRow(key)}
          aria-expanded={open}
          aria-label={
            open
              ? t('reports.clockings.collapseRow', { day })
              : t('reports.clockings.expandRow', { day })
          }
          data-testid={`clockings-expand-${row.userId}-${day}`}
          className="shrink-0 rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
          )}
        </button>
        <span>{formatDayShort(day, lang)}</span>
      </span>
    );
  };

  const renderStatus = (row: ClockingsDailyRowView) => (
    <StatusChip
      tone={dayStatusTone(row.dayStatus)}
      data-testid={`clockings-status-${row.dayStatus}`}
      title={
        row.lateIn
          ? t('reports.clockings.lateByHint', { value: formatMinutes(row.lateMinutes) })
          : row.earlyOut
            ? t('reports.clockings.earlyByHint', { value: formatMinutes(row.earlyOutMinutes) })
            : undefined
      }
    >
      {t(`reports.dayStatus.${row.dayStatus}`)}
    </StatusChip>
  );

  const renderLastOut = (row: ClockingsDailyRowView) => {
    if (row.missingOut && !row.lastOut) {
      return (
        <StatusChip tone="critical" icon={LogOut} data-testid="clockings-missing-out-cell">
          {t('reports.clockings.flag.MissingOut')}
        </StatusChip>
      );
    }
    // An overnight day clocks out on the NEXT calendar day, and a bare "06:00" next to a "22:00"
    // start reads as a 16-hour negative. The marker is the whole difference.
    const overnight = !!row.lastOut && dayKeyOf(row.lastOut) !== dayKeyOf(row.date);
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {timeCell(row.lastOut)}
        {overnight && (
          <span className="text-xs text-muted-foreground" title={t('reports.clockings.nextDayHint')}>
            {t('reports.clockings.nextDay')}
          </span>
        )}
      </span>
    );
  };

  const renderFlags = (row: ClockingsDailyRowView) => {
    const flags = flagsOf(row);
    if (flags.length === 0) return <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>;
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {flags.map((flag) => (
          <StatusChip
            key={flag}
            tone={FLAG_TONE[flag]}
            icon={FLAG_ICON[flag]}
            data-testid={`clockings-flag-${flag}`}
          >
            {t(`reports.clockings.flag.${flag}`)}
          </StatusChip>
        ))}
      </span>
    );
  };

  /**
   * THE PUNCH TIMELINE — everything the old per-punch table showed, as the DETAIL of a day rather
   * than as the report itself. Rendered only for an expanded row, so a hundred-day report does not
   * carry a thousand hidden nodes.
   */
  const renderPunchTimeline = (row: ClockingsDailyRowView) => {
    if (!expanded[rowKeyOf(row)]) return null;
    const punches: ClockingPunchView[] = row.punches ?? [];
    const day = dayKeyOf(row.date);
    return (
      <div className="px-4 py-3" data-testid={`clockings-timeline-${row.userId}-${day}`}>
        {punches.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('reports.clockings.noPunches')}</p>
        ) : (
          <ol className="space-y-1.5">
            {punches.map((punch, index) => (
              <li
                key={`${punch.type}-${punch.id}-${index}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs"
              >
                <span className="w-12 shrink-0 tabular-nums font-medium text-foreground">
                  {wallClock(punch.time) ?? EMPTY_VALUE}
                </span>
                <span className="w-24 shrink-0 text-muted-foreground-strong">
                  {punchLabel(punch.type)}
                </span>
                <span className="w-16 shrink-0 text-muted-foreground">
                  {sourceLabel(punch.clientType)}
                </span>
                {punch.address && (
                  <span className="min-w-0 flex-1 truncate text-muted-foreground" title={punch.address}>
                    {punch.address}
                  </span>
                )}
                {punch.wasOutOfBounds && (
                  <StatusChip
                    tone="warning"
                    icon={MapPinOff}
                    title={[
                      punch.outOfBoundsDistanceMeters != null
                        ? t('reports.clockings.outOfBoundsDistance', { meters: punch.outOfBoundsDistanceMeters })
                        : t('reports.clockings.flag.OutOfBounds'),
                      punch.outOfBoundsReason
                        ? t('reports.clockings.outOfBoundsReason', { reason: punch.outOfBoundsReason })
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  >
                    {t('reports.clockings.flag.OutOfBounds')}
                  </StatusChip>
                )}
                {punch.comments && (
                  <span className="min-w-0 flex-1 truncate text-muted-foreground/80" title={punch.comments}>
                    {punch.comments}
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  /* ── columns ──────────────────────────────────────────────────────────────────────────────────
   *
   * 🔴 THE `key` OF EACH COLUMN IS THE EXPORT CONTRACT. `reports/Export/Clockings` writes its row
   * dictionary under exactly these names (date, status, scheduledStart, scheduledEnd, firstIn,
   * lastOut, worked, breaks, punches, source, flags), so renaming one here silently blanks a column
   * in the file.
   *
   * Identity and verdict first, then the plan, then what actually happened, then the figures —
   * which is also what gives the group band its width: `ReportTable` lets a group header span up to
   * the first column carrying a subtotal.
   */
  const columns = useMemo<ReportColumn<ClockingsDailyRowView>[]>(
    () => [
      {
        key: 'date',
        label: t('reports.clockings.date'),
        kind: 'date',
        width: '12rem',
        emphasis: true,
        value: (r) => dayKeyOf(r.date),
        render: renderDate,
      },
      {
        key: 'status',
        label: t('reports.clockings.status'),
        kind: 'status',
        value: (r) => r.dayStatus,
        render: renderStatus,
      },
      {
        key: 'scheduledStart',
        label: t('reports.clockings.scheduledStart'),
        kind: 'text',
        align: 'start',
        muted: true,
        hideBelow: 'lg',
        value: (r) => wallClock(r.scheduledStart) ?? '',
        render: (r) => timeCell(r.scheduledStart),
      },
      {
        key: 'scheduledEnd',
        label: t('reports.clockings.scheduledEnd'),
        kind: 'text',
        align: 'start',
        muted: true,
        hideBelow: 'lg',
        value: (r) => wallClock(r.scheduledEnd) ?? '',
        render: (r) => timeCell(r.scheduledEnd),
      },
      {
        key: 'firstIn',
        label: t('reports.clockings.firstIn'),
        kind: 'text',
        align: 'start',
        value: (r) => wallClock(r.firstIn) ?? '',
        render: (r) => timeCell(r.firstIn),
      },
      {
        key: 'lastOut',
        label: t('reports.clockings.lastOut'),
        kind: 'text',
        align: 'start',
        value: (r) => wallClock(r.lastOut) ?? '',
        render: renderLastOut,
      },
      {
        key: 'worked',
        label: t('reports.clockings.worked'),
        kind: 'hours',
        total: 'sum',
        tooltip: t('reports.clockings.workedHint'),
        value: (r) => workedHoursOf(r),
        render: (r) => hoursCell(workedHoursOf(r), 'font-medium'),
      },
      {
        key: 'breaks',
        label: t('reports.clockings.breaks'),
        kind: 'minutes',
        total: 'sum',
        hideBelow: 'md',
        tooltip: t('reports.clockings.breaksHint'),
        value: (r) => r.breakMinutes ?? 0,
        // The COUNT next to the minutes, because "3 (45m)" and "1 (45m)" are different days and the
        // minutes alone cannot tell them apart.
        render: (r) =>
          (r.breakCount ?? 0) > 0 ? (
            <span className="text-muted-foreground">
              {formatCountWithMinutes(r.breakCount, r.breakMinutes, lang)}
            </span>
          ) : (
            <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>
          ),
      },
      {
        key: 'punches',
        label: t('reports.clockings.punches'),
        kind: 'int',
        total: 'sum',
        hideBelow: 'lg',
        tooltip: t('reports.clockings.punchesHint'),
        value: (r) => r.punchCount ?? 0,
      },
      {
        key: 'source',
        label: t('reports.clockings.source'),
        kind: 'text',
        align: 'start',
        muted: true,
        hideBelow: 'lg',
        // The clock-IN device. The clock-out's device (and every break's) is in the timeline, which
        // is where a per-punch fact belongs — a column can only hold one of them, and inventing a
        // "Web → Mobile" composite would be a string the export could not reproduce.
        value: (r) => r.inClientType ?? '',
        render: (r) =>
          r.inClientType ? sourceLabel(r.inClientType) : <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>,
      },
      {
        key: 'flags',
        label: t('reports.clockings.flags'),
        kind: 'text',
        align: 'start',
        value: (r) => flagsOf(r).map((f) => t(`reports.clockings.flag.${f}`)).join(', '),
        render: renderFlags,
      },
    ],
    // The render closures are re-created every render, so listing them would defeat the memo. What
    // they READ is listed instead — the language (dates, chips, labels) and the expanded set (the
    // date column's chevron). Anything added to those closures must be added here too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang, expanded],
  );

  /* ── summary strip ────────────────────────────────────────────────────────────────────────── */

  const summaryItems = useMemo<ReportSummaryItem[]>(() => {
    if (rows.length === 0) return [];
    return [
      {
        label: t('reports.clockings.employees'),
        value: formatInt(groups.length, lang),
        icon: Users,
        testId: 'clockings-employees',
      },
      {
        label: t('reports.clockings.days'),
        value: formatInt(totals.days, lang),
        icon: CalendarDays,
        testId: 'clockings-days',
      },
      {
        label: t('reports.clockings.worked'),
        value: formatHours(totals.worked),
        icon: Timer,
        testId: 'clockings-worked-total',
      },
      {
        label: t('reports.clockings.missingOut'),
        value: formatInt(totals.missingOut, lang),
        hint: totals.missingOut > 0 ? t('reports.clockings.missingOutHint') : undefined,
        // The one figure on this strip that is an ACTION, not an observation: a day with no
        // clock-out is a row somebody has to fix before the month can be trusted.
        tone: totals.missingOut > 0 ? 'critical' : 'default',
        icon: LogOut,
        testId: 'clockings-missing-out',
      },
      {
        label: t('reports.clockings.lateIns'),
        value: formatInt(totals.lateIn, lang),
        tone: totals.lateIn > 0 ? 'warning' : 'default',
        icon: AlarmClock,
        testId: 'clockings-late-in',
      },
    ];
  }, [rows.length, groups.length, totals, lang, t]);

  /* ── export ───────────────────────────────────────────────────────────────────────────────── */

  const periodText = formatPeriod(period.from, period.to, lang);
  const scopeText = scope.scopeText;

  /**
   * The client does NOT render the file. It posts the SAME filter the screen ran plus a description
   * of the columns, and the server re-runs the query and renders the document — so the file and the
   * screen cannot disagree about a figure, and all three formats come from one pipeline instead of
   * the in-browser workbook `reports/Clockings/{format}` produced. That legacy route is still live
   * for mobile; this tab no longer calls it.
   */
  const onExport = (format: ReportExportFormat) => {
    const title = t('reports.clockings.title');
    const request = buildExportRequest<ClockingsReportInput, ClockingsDailyRowView>({
      input: buildInput(),
      format,
      title,
      columns,
      culture: lang,
      rtl: i18n.dir() === 'rtl',
      labels: { ...reportExportLabels(t), period: periodText, scope: scopeText },
      lookups: {
        // Raw token -> the words the reader sees, so the file reads in their language. The server
        // derives the SAME tokens and looks them up here.
        status: {
          Present: t('reports.dayStatus.Present'),
          Late: t('reports.dayStatus.Late'),
          EarlyLeave: t('reports.dayStatus.EarlyLeave'),
          Unscheduled: t('reports.dayStatus.Unscheduled'),
        },
        source: {
          Web: t('reports.clockings.source.Web'),
          Mobile: t('reports.clockings.source.Mobile'),
        },
        flags: {
          MissingOut: t('reports.clockings.flag.MissingOut'),
          OutOfBounds: t('reports.clockings.flag.OutOfBounds'),
          Edited: t('reports.clockings.flag.Edited'),
        },
      },
    });
    void exportReport(
      format,
      () => postReportExport('reports/Export/Clockings', request),
      reportExportFilename(title, periodText, format),
    );
  };

  /* ── render ───────────────────────────────────────────────────────────────────────────────── */

  const filterChips = hasRun
    ? [
        { key: 'period', label: periodText, tone: 'accent' as const },
        { key: 'scope', label: scopeText },
      ]
    : [];

  return (
    <PageLayout
      title={t('reports.clockings.title')}
      description={t('reports.clockings.subtitle')}
      icon={Clock}
      accentColor="blue"
      maxWidth="1600px"
    >
      <div className="space-y-6">
        <ReportFilterBar
          testId="clockings-filters"
          chips={filterChips}
          actions={
            <>
              <RunButton
                testId="generate-report"
                loading={loading}
                disabled={loading || invalidRange}
                onClick={() => void search()}
              />
              <ExportMenu
                onExport={onExport}
                busy={exporting}
                disabled={!hasRun || rows.length === 0}
              />
            </>
          }
        >
          <ReportFilterField label={t('reports.clockings.dateRange')}>
            <PeriodPicker mode="day" value={period} onChange={setPeriod} disabled={loading} />
          </ReportFilterField>

          <ReportFilterField label={t('reports.scope')} className="max-w-4xl">
            <ScopeFilter scope={scope} disabled={loading} testId="scope-filter" />
          </ReportFilterField>
        </ReportFilterBar>

        {loading ? (
          <ReportLoading columns={columns.length} />
        ) : error ? (
          <ReportError message={error} onRetry={() => void search()} />
        ) : !hasRun ? (
          <ReportIdle hint={t('reports.clockings.idleHint')} />
        ) : (
          <div className="space-y-4">
            <ReportSummaryStrip
              items={summaryItems}
              testId="summary-clockings"
              ariaLabel={t('reports.clockings.title')}
            />

            {/* Said once, so nobody has to reverse-engineer why a day with an unfinished shift
                shows fewer worked hours than the punches suggest. */}
            {rows.length > 0 && (
              <p className="max-w-[80ch] text-xs text-muted-foreground">
                {t('reports.clockings.rowIsADay')}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => (collapse.allCollapsed ? collapse.expandAll() : collapse.collapseAll())}
                data-testid="clockings-collapse-toggle"
                disabled={groups.length === 0}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground-strong transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                {collapse.allCollapsed ? t('reports.expandAll') : t('reports.collapseAll')}
              </button>
            </div>

            <ReportTable
              columns={columns}
              groups={groups}
              collapse={collapse}
              rowKey={rowKeyOf}
              rowTestId={(r) => `clockings-row-${r.userId}-${dayKeyOf(r.date)}`}
              renderRowDetail={renderPunchTimeline}
              density="compact"
              emptyState={<ReportEmpty period={periodText} scopeText={scopeText} />}
              testId="clockings-table"
              ariaLabel={t('reports.clockings.title')}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
}
